<script>
    import { getContext } from 'svelte';
    import { Policy, User, Language } from "@Store";
    import Auth from "@components/Auth.svelte";
    import { push } from 'svelte-spa-router';
    import { to } from "@API";

    let { policyID } = getContext("CONTEXT");

    function onLoginSuccess() {
        $User = $User;
    }

    function formatCurrency(amount) {
        const n = parseFloat(amount || 0);
        if (isNaN(n)) return '$0.00';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
    }

    async function handleDownload(certificateType) {
        try {
            const policyId = $Policy?.id;
            if (!policyId) {
                throw new Error('Policy ID not found');
            }
            
            const token = localStorage.getItem("insurecert_token");
            const url = `/api/dashboard/policies/${policyId}/download_custom_certificate/`;
            
            // Fetch the PDF as a blob
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': token ? `JWT ${token}` : '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    certificate_type: certificateType
                })
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                
                // Extract filename from content-disposition header if available
                const contentDisposition = response.headers.get('content-disposition');
                const filename = contentDisposition 
                    ? contentDisposition.split('filename=')[1]?.replace(/['"]/g, '')
                    : `${certificateType.replace(' ', '-')}-${$Policy?.certificate_number}.pdf`;
                
                // Create temporary link and trigger download
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(objectUrl);
                
                console.log(`Downloaded ${certificateType} successfully.`);
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Download failed: ${response.status}`);
            }
        } catch (error) {
            console.error(`Error downloading ${certificateType}:`, error);
            alert($Language === 'en' 
                ? `Unable to download document: ${error.message}` 
                : `Impossible de télécharger le document: ${error.message}`);
        }
    }

    function downloadStaticFile(filePath, fileName) {
        try {
            const a = document.createElement('a');
            a.href = filePath;
            a.download = fileName;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            console.log(`Downloaded ${fileName} successfully.`);
        } catch (error) {
            console.error(`Error downloading ${fileName}:`, error);
            alert($Language === 'en' 
                ? `Unable to download document: ${error.message}` 
                : `Impossible de télécharger le document: ${error.message}`);
        }
    }

    $: namedValuesMap = ($Policy.saved_named_values || []).reduce((map, nv) => {
        map[nv.name] = nv.value?.trim() || '';
        return map;
    }, {});

    $: arag_required = namedValuesMap['legal_required']?.toLowerCase() === 'yes';
    $: has_legal = namedValuesMap['legal_certificate']?.toLowerCase() === 'true';
    $: has_cgl_individual_medmal = namedValuesMap['cgl_individual_medmal_certificate']?.toLowerCase() === 'true';
    $: has_individual_medmal = namedValuesMap['individual_medmal_certificate']?.toLowerCase() === 'true';
    $: has_clinic_medmal = namedValuesMap['clinic_medmal_certificate']?.toLowerCase() === 'true';
    $: has_acupuncture = namedValuesMap['acupuncture_certificate']?.toLowerCase() === 'true';
    $: insured_province = namedValuesMap['insured_province']?.toLowerCase() || '';

    // Province to Office Package policy number mapping
    const officePackageMap = {
        'bc': 7,
        'mb': 9,
        'ab': 10,
        'nl': 11,
        'ns': 12,
        'nu': 13,
        'nwt': 14,
        'on': 15,
        'qu': 17,
        'sk': 18,
        'yk': 19,
        'nb': 20,
        'pe': 21   
    };

    // Helper function to get Office Package policy number
    function getOfficePackagePolicyNumber(province) {
        const normalizedProvince = province?.toLowerCase().trim();
        return officePackageMap[normalizedProvince];
    }

    // Check if province has an office package
    $: hasOfficePackage = !!getOfficePackagePolicyNumber(insured_province);
    $: officePackagePolicyNumber = getOfficePackagePolicyNumber(insured_province);


    $: premium = $Policy?.premium ?? 0;
    $: taxTotal = $Policy?.taxtotal ?? 0;
    $: serviceFee = $Policy?.service_fee ?? 0;
    // $: subtotal = $Policy?.subtotal ?? (premium + taxTotal + serviceFee);
    $: total = $Policy?.total ?? 0;
</script>

<div class="complete-page">
    {#if $User}
        <!-- Authenticated view: full details -->
        <div class="hero">
            <div class="icon-wrap">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M8 12.5l2.5 2.5L16 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <h1>{$Language === 'en' ? 'Policy Completed' : 'Politique terminée'}</h1>
            <p class="sub">
                {$Language === 'en' ? 'Your coverage is now active. We’ve sent a confirmation email to' : 'Votre couverture est maintenant active. Nous avons envoyé un email de confirmation à'}
                <strong>{$Policy?.billing_info?.email || 'your email'}</strong>.
            </p>
        </div>

        <div class="cards">
            <!-- What happens next -->
            <div class="card">
                <h2>What happens next</h2>
                <ul class="bullets">
                    <li>You’ll should have received your policy documents and receipt by email. Check your spam folder if not found.</li>
                    <li>If your policy includes Cyber Coverage, your certificate will be emailed to you once activation is finalized.</li>
                    <li>You can view and download your documents anytime from your dashboard.</li>
                    <li><a class='link' href='https://cand.ipmgainsurance.com/policy-wording/' target='_blank'>Click here for all policy wordings</a></li>
                </ul>
            </div>

            <!-- Policy details -->
            <div class="card highlight">
                <h2>Policy details</h2>
                <div class="grid">
                    <div class="row">
                        <span class="label">Policy ID</span>
                        <span class="value">{$Policy?.policy_number}</span>
                    </div>
                    <div class="row">
                        <span class="label">Status</span>
                        <span class="value status">{($Policy?.status || '').toUpperCase()}</span>
                    </div>
                    <div class="row">
                        <span class="label">Premium</span>
                        <span class="value">${premium}</span>
                    </div>
                    <div class="row">
                        <span class="label">Taxes</span>
                        <span class="value">${taxTotal}</span>
                    </div>
                    <div class="row">
                        <span class="label">Service Fee</span>
                        <span class="value">{formatCurrency(serviceFee)}</span>
                    </div>
                    <div class="row total">
                        <span class="label">Total</span>
                        <span class="value emphasis">${total}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Downloads -->
        <div class="card">
            <h2>{$Language === 'en' ? 'Policy Documents' : 'Documents de police'}</h2>
            <div class="downloads-grid">
                <!-- legal services certificate -->
                {#if arag_required}
                    <button class="download-row" on:click={() => handleDownload('Policy 22')}>
                        <div class="doc-info">
                            <svg class="doc-icon" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" stroke-width="2"/>
                                <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <div class="doc-text">
                                <span class="doc-name">{$Language === 'en' ? 'Legal Expense Certificate' : 'Certificat de dépenses juridiques'}</span>
                                <span class="doc-desc">{$Language === 'en' ? 'Legal expense coverage certificate' : 'Certificat de couverture des dépenses juridiques'}</span>
                            </div>
                        </div>
                    </button>
                {/if}
                {#if has_legal}
                    <button class="download-row" on:click={() => handleDownload('Policy 6')}>
                        <div class="doc-info">
                            <svg class="doc-icon" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" stroke-width="2"/>
                                <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <div class="doc-text">
                                <span class="doc-name">{$Language === 'en' ? 'Legal Services Certificate' : 'Certificat de services juridiques'}</span>
                                <span class="doc-desc">{$Language === 'en' ? 'Legal services coverage certificate' : 'Certificat de couverture des services juridiques'}</span>
                            </div>
                        </div>
                        <span class="download-label">Download</span>
                    </button>
                {/if}

                {#if has_individual_medmal}
                    <button class="download-row" on:click={() => handleDownload('Policy 3')}>
                        <div class="doc-info">
                            <svg class="doc-icon" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" stroke-width="2"/>
                                <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <div class="doc-text">
                                <span class="doc-name">{$Language === 'en' ? 'Individual Medical Malpractice Certificate' : 'Certificat de malpratique médicale individuelle'}</span>
                                <span class="doc-desc">{$Language === 'en' ? 'Individual medical malpractice coverage certificate' : 'Certificat de couverture de malpratique médicale individuelle'}</span>
                            </div>
                        </div>
                        <span class="download-label">Download</span>
                    </button>
                {/if}

                {#if has_cgl_individual_medmal}
                    <button class="download-row" on:click={() => handleDownload('Policy 5')}>
                        <div class="doc-info">
                            <svg class="doc-icon" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" stroke-width="2"/>
                                <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <div class="doc-text">
                                <span class="doc-name">{$Language === 'en' ? 'CAND Medical Malpractice Certificate CGL' : 'Certificat de malpratique médicale CAND CGL'}</span>
                                <span class="doc-desc">{$Language === 'en' ? 'CAND medical malpractice coverage certificate for CGL' : 'Certificat de couverture de malpratique médicale CAND pour CGL'}</span>
                            </div>
                        </div>
                        <span class="download-label">Download</span>
                    </button>
                {/if}

                {#if has_clinic_medmal}
                    <button class="download-row" on:click={() => handleDownload('Policy 4')}>
                        <div class="doc-info">
                            <svg class="doc-icon" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" stroke-width="2"/>
                                <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <div class="doc-text">
                                <span class="doc-name">{$Language === 'en' ? 'Clinic Medical Malpractice Certificate' : 'Certificat de malpratique médicale de clinique'}</span>
                                <span class="doc-desc">{$Language === 'en' ? 'Clinic medical malpractice coverage certificate' : 'Certificat de couverture de malpratique médicale de clinique'}</span>
                            </div>
                        </div>
                        <span class="download-label">Download</span>
                    </button>
                {/if}
                
                {#if has_acupuncture}
                    <button class="download-row" on:click={() => handleDownload('Policy 2')}>
                        <div class="doc-info">
                            <svg class="doc-icon" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" stroke-width="2"/>
                                <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <div class="doc-text">
                                <span class="doc-name">{$Language === 'en' ? 'Acupuncture Endorsement Certificate' : 'Certificat de la certification d\'acupuncture'}</span>
                                <span class="doc-desc">{$Language === 'en' ? 'Acupuncture endorsement coverage certificate' : 'Certificat de couverture de la certification d\'acupuncture'}</span>
                            </div>
                        </div>
                        <span class="download-label">Download</span>
                    </button>
                {/if}

                {#if hasOfficePackage}
                    <button class="download-row" on:click={() => handleDownload(`Policy ${officePackagePolicyNumber}`)}>
                        <div class="doc-info">
                            <svg class="doc-icon" viewBox="0 0 24 24" fill="none">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" stroke-width="2"/>
                                <path d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                            <div class="doc-text">
                                <span class="doc-name">
                                    {$Language === 'en' ? 'Office Package Certificate' : 'Certificat de forfait de bureau'}
                                </span>
                                <span class="doc-desc">
                                    {$Language === 'en' 
                                        ? `Office package coverage certificate for ${insured_province.toUpperCase()}` 
                                        : `Certificat de couverture du forfait de bureau pour ${insured_province.toUpperCase()}`}
                                </span>
                            </div>
                        </div>
                        <span class="download-label">Download</span>
                    </button>
                {/if}

                
            </div>
        </div>

        <div class="wordings">
            <div class="wording-card">
                <h3>Additional Notes</h3>
                <ul>
                    <li>If Cyber Coverage is included, your cyber certificate will be emailed once the carrier confirms activation.</li>
                    <li>For Directors & Officers coverage, onboarding details and next steps will be provided by email within 1–2 business days.</li>
                    <li>Property schedules (if applicable) will be attached to your policy package and available in your dashboard.</li>
                </ul>
            </div>
        </div>

        <div class="actions">
            <button class="primary" on:click={() => push('/dashboard')}>Go to Dashboard</button>
        </div>
    {:else}
        <!-- Unauthenticated view: no policy data rendered -->
        <div class="secure-guard">
            <div class="lock-icon">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="4" y="10" width="16" height="10" rx="2" stroke="currentColor" stroke-width="2"/>
                    <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
            </div>
            <h1>Access your policy</h1>
            <p class="secure-sub">Please log in to view policy details and documents.</p>

            <div class="auth-card">
                <h3>Create your account or log in</h3>
                <p class="small">Manage your policy, download documents, and request changes anytime.</p>
                <Auth 
                    billingEmail={$Policy?.billing_info?.email}
                    onLoginSuccess={onLoginSuccess}
                />
            </div>
        </div>
    {/if}
</div>

<style>
    .complete-page {
        max-width: 860px;
        margin: 0 auto;
        padding: 2rem 1rem 3rem;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    }

    /* Hero */
    .hero {
        text-align: center;
        margin-bottom: 1.75rem;
    }

    .icon-wrap {
        width: 72px;
        height: 72px;
        margin: 0 auto 1rem;
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: #fff;
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.25);
    }
    .icon-wrap svg {
        width: 36px;
        height: 36px;
    }

    h1 {
        margin: 0 0 0.5rem 0;
        font-size: 1.9rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .sub {
        margin: 0;
        color: #666;
    }

    /* Cards */
    .cards {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
        margin-bottom: 1.25rem;
    }

    .card {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 20px;
        padding: 1.25rem 1.25rem 1.25rem;
        box-shadow: 0 4px 20px rgba(0,0,0,0.04);
    }

    .card h2 {
        margin: 0 0 0.75rem 0;
        font-size: 1.2rem;
        font-weight: 700;
        color: #1d1d1f;
        letter-spacing: -0.02em;
    }

    .highlight {
        background: linear-gradient(135deg, #fffbf0 0%, #ffffff 100%);
        border-color: #f5d99f;
        box-shadow: 0 4px 20px rgba(227, 161, 37, 0.12);
    }

    .bullets {
        margin: 0;
        padding: 0 0 0 1.25rem;
        color: #1d1d1f;
        line-height: 1.6;
    }

    /* Policy details grid */
    .grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.5rem;
    }

    .row {
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        padding: 0.625rem 0;
        border-bottom: 1px solid #f0f0f0;
    }
    .row:last-child {
        border-bottom: none;
    }

    .row.total {
        border-top: 2px solid var(--gold);
        margin-top: 0.25rem;
        padding-top: 0.9rem;
    }

    .label {
        color: #6b7280;
        font-weight: 600;
        letter-spacing: -0.01em;
    }

    .link {
        color: var(--base-green);
        text-decoration: underline;
    }

    .value {
        color: #1d1d1f;
        font-weight: 600;
    }

    .value.status {
        color: var(--base-green);
        font-weight: 700;
    }

    .value.emphasis {
        color: var(--gold);
        font-weight: 800;
        font-size: 1.125rem;
    }

    /* Wordings */
    .wordings {
        margin: 1.25rem 0 1.5rem;
    }
    .wording-card {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 16px;
        padding: 1rem 1rem;
    }
    .wording-card h3 {
        margin: 0 0 0.5rem 0;
        font-size: 1.05rem;
        font-weight: 700;
        color: #1d1d1f;
    }
    .wording-card ul {
        margin: 0;
        padding-left: 1.125rem;
        color: #1d1d1f;
        line-height: 1.6;
    }

    /* Auth section */
    .auth-section {
        margin-top: 1.25rem;
    }
    .auth-card {
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 16px;
        padding: 1rem 1rem;
        box-shadow: 0 4px 20px rgba(0,0,0,0.04);
    }
    .auth-card h3 {
        margin: 0 0 0.25rem 0;
        font-weight: 700;
        color: #1d1d1f;
    }
    .auth-card .small {
        color: #6b7280;
        margin: 0 0 0.75rem 0;
    }

    /* Actions */
    .actions {
        display: flex;
        gap: 0.75rem;
        align-items: center;
        margin-top: 1.25rem;
    }

    .primary {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 14px;
        padding: 0.875rem 1.5rem;
        cursor: pointer;
        font-size: 1rem;
        font-weight: 700;
        color: #fff;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        letter-spacing: -0.01em;
        box-shadow: 0 6px 20px rgba(49, 104, 50, 0.25);
    }
    .primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 28px rgba(49, 104, 50, 0.35);
    }

    .secondary {
        background: transparent;
        border: 2px solid #e9ecef;
        color: #1d1d1f;
        border-radius: 14px;
        padding: 0.875rem 1.25rem;
        cursor: pointer;
        font-weight: 700;
        transition: all 0.2s ease;
    }
    .secondary:hover {
        background: rgba(172, 212, 172, 0.15);
        border-color: var(--base-green);
        color: var(--base-green);
    }

    .secure-guard {
        max-width: 640px;
        margin: 0 auto;
        text-align: center;
    }

    .lock-icon {
        width: 64px;
        height: 64px;
        margin: 0 auto 1rem;
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: #fff;
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        box-shadow: 0 8px 24px rgba(49, 104, 50, 0.25);
    }
    .lock-icon svg { width: 34px; height: 34px; }

    .secure-sub { color: #666; margin: 0 0 1rem 0; }

    /* Downloads grid */
    .downloads-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0.75rem;
        margin-top: 1rem;
    }

    .download-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border: 2px solid #e9ecef;
        border-radius: 14px;
        padding: 0.875rem 1rem;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 4px 15px rgba(0,0,0,0.04);
    }
    .download-row:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0,0,0,0.08);
    }

    .doc-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }
    .doc-icon {
        width: 24px;
        height: 24px;
        color: var(--base-green);
    }
    .doc-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }
    .doc-name {
        font-weight: 600;
        color: #1d1d1f;
        font-size: 0.95rem;
    }
    .doc-desc {
        font-size: 0.8rem;
        color: #6b7280;
    }
    .download-label {
        background: linear-gradient(135deg, var(--base-green) 0%, var(--base-green-light) 100%);
        border: 2px solid var(--base-green);
        border-radius: 10px;
        padding: 0.4rem 0.8rem;
        font-size: 0.85rem;
        font-weight: 700;
        color: #fff;
        white-space: nowrap;
    }

    @media (min-width: 700px) {
        .cards {
            grid-template-columns: 1.25fr 1fr;
        }
    }
</style>